import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const statusBadgeVariants = cva(
  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border",
  {
    variants: {
      variant: {
        success: "bg-success-100 text-success-700 border-success-500/20",
        warning: "bg-warning-100 text-warning-700 border-warning-500/20",
        info: "bg-info-100 text-info-700 border-info-500/20",
        danger: "bg-destructive/10 text-destructive border-destructive/20",
        neutral: "bg-muted text-muted-foreground border-border",
      },
      status: {
        // User statuses
        active: "bg-success-100 text-success-700 border-success-500/20",
        pending: "bg-warning-100 text-warning-700 border-warning-500/20",
        invited: "bg-info-100 text-info-700 border-info-500/20",
        disabled: "bg-muted text-muted-foreground border-border",

        // Asset lifecycle statuses
        draft: "bg-slate-100 text-slate-700 border-slate-500/20 dark:bg-slate-800/20 dark:text-slate-300",
        inventoried: "bg-info-100 text-info-700 border-info-500/20",
        valued: "bg-purple-100 text-purple-700 border-purple-500/20 dark:bg-purple-900/20 dark:text-purple-300",
        'listed-for-sale': "bg-success-100 text-success-700 border-success-500/20",
        'under-offer': "bg-yellow-100 text-yellow-700 border-yellow-500/20 dark:bg-yellow-900/20 dark:text-yellow-300",
        sold: "bg-cyan-100 text-cyan-700 border-cyan-500/20 dark:bg-cyan-900/20 dark:text-cyan-300",
        'payment-pending': "bg-orange-100 text-orange-700 border-orange-500/20 dark:bg-orange-900/20 dark:text-orange-300",
        delivered: "bg-teal-100 text-teal-700 border-teal-500/20 dark:bg-teal-900/20 dark:text-teal-300",
        disposed: "bg-gray-100 text-gray-700 border-gray-500/20 dark:bg-gray-800/20 dark:text-gray-300",
        archived: "bg-muted text-muted-foreground border-border",
      }
    },
    defaultVariants: {
      variant: "neutral",
    },
  }
)

export interface StatusBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusBadgeVariants> {
  children: React.ReactNode
}

/**
 * Status badge component with semantic color variants
 *
 * Use semantic variants (success, warning, info, danger) for general purposes,
 * or specific status variants (active, pending, inventory, etc.) for domain-specific states.
 *
 * @example
 * <StatusBadge status="active">Active</StatusBadge>
 * <StatusBadge variant="success">Completed</StatusBadge>
 * <StatusBadge status="inventory">In Stock</StatusBadge>
 */
export function StatusBadge({
  variant,
  status,
  children,
  className,
  ...props
}: StatusBadgeProps) {
  return (
    <span
      className={cn(statusBadgeVariants({ variant, status }), className)}
      {...props}
    >
      {children}
    </span>
  )
}
